/**
 * A client's suppression list, and the switch that turns it on for the client.
 *
 * Off by default, and off means the client has no trace of it: nothing in its
 * portal, nothing checked on its sends, nothing learned from delivery reports.
 * So the switch is the first thing on this card, and the list below it says
 * plainly when it is not being applied.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldBan, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  QueryPending,
  ReadOnly,
  Select,
  ServerTable,
  Switch,
  Textarea,
  api,
  createServerFetcher,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { messagingKeys } from './types';

interface SuppressionSettings {
  enabled: boolean;
  count: number;
}

type SuppressionRow = {
  public_id: string;
  mobile: string;
  reason: 'manual' | 'stop' | 'dnd' | string;
  note: string | null;
  added_by: 'client' | 'staff' | 'delivery' | string;
  created_at: string;
};

interface AddResult {
  added: number;
  already_suppressed: number;
  invalid: string[];
}

const REASON_LABELS: Record<string, string> = {
  manual: 'Blocked',
  stop: 'Opted out',
  dnd: 'Do Not Disturb',
};
const REASON_TONES: Record<string, Tone> = { manual: 'neutral', stop: 'warning', dnd: 'info' };
const ADDED_BY_LABELS: Record<string, string> = {
  client: 'The client',
  staff: 'Staff',
  delivery: 'Delivery report',
};

export function SuppressionCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.suppression.edit');
  const [numbers, setNumbers] = useState('');
  const [reason, setReason] = useState<'manual' | 'stop'>('manual');
  const [removing, setRemoving] = useState<SuppressionRow | null>(null);
  const base = `/messaging/clients/${clientPublicId}`;
  const listKey = useMemo(() => [...messagingKeys.suppressions(clientPublicId), 'list'] as const, [clientPublicId]);

  const settings = useQuery({
    queryKey: messagingKeys.suppressions(clientPublicId),
    queryFn: () => api.get<SuppressionSettings>(`${base}/suppression`),
  });
  const fetchRows = useMemo(() => createServerFetcher<SuppressionRow>(`${base}/suppressions`), [base]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: messagingKeys.suppressions(clientPublicId) });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put<SuppressionSettings>(`${base}/suppression`, { enabled }),
    onSuccess: (result) => {
      queryClient.setQueryData(messagingKeys.suppressions(clientPublicId), result);
      toast.success(
        result.enabled ? 'Suppression list on' : 'Suppression list off',
        result.enabled
          ? `${clientName}'s sends now skip the numbers on it.`
          : `${clientName} no longer sees it, and sends do not check it.`,
      );
    },
    onError: (error) => toast.error('Could not change it', error instanceof ApiError ? error.message : undefined),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<AddResult>(`${base}/suppressions`, {
        numbers: numbers.split(/[\s,;]+/).filter(Boolean),
        reason,
      }),
    onSuccess: (result) => {
      setNumbers('');
      refresh();
      toast.success(
        `${formatNumber(result.added)} added`,
        [
          result.already_suppressed ? `${formatNumber(result.already_suppressed)} were already on it` : null,
          result.invalid.length ? `not mobile numbers: ${result.invalid.join(', ')}` : null,
        ]
          .filter(Boolean)
          .join('; ') || undefined,
      );
    },
  });

  const remove = useMutation({
    mutationFn: (row: SuppressionRow) => api.delete<null>(`${base}/suppressions/${row.public_id}`),
    onSuccess: () => {
      refresh();
      toast.success('Removed', 'Messages go to that number again.');
    },
  });

  const columns = useMemo<ServerColumn<SuppressionRow>[]>(
    () => [
      { field: 'mobile', header: 'Mobile', width: 150 },
      {
        field: 'reason',
        header: 'Why',
        width: 150,
        render: (row) => <Badge tone={REASON_TONES[row.reason] ?? 'neutral'}>{REASON_LABELS[row.reason] ?? row.reason}</Badge>,
      },
      {
        field: 'added_by',
        header: 'Added by',
        width: 150,
        render: (row) => ADDED_BY_LABELS[row.added_by] ?? row.added_by,
      },
      {
        field: 'created_at',
        header: 'Added',
        width: 170,
        render: (row) => formatDateTime(row.created_at),
      },
      {
        field: 'note',
        header: 'Note',
        sortable: false,
        render: (row) => <span className="text-muted">{row.note}</span>,
      },
      {
        field: 'public_id',
        header: '',
        width: 60,
        sortable: false,
        render: (row) => (
          <Can permission="messaging.suppression.delete">
            <Button variant="ghost" size="sm" aria-label={`Remove ${row.mobile}`} onClick={() => setRemoving(row)}>
              <Trash2 className="size-3.5" />
            </Button>
          </Can>
        ),
      },
    ],
    [],
  );

  if (settings.data === undefined) return <QueryPending query={settings} label="Loading the suppression list" />;
  const enabled = settings.data?.enabled ?? false;

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <ShieldBan className="size-4 text-muted" />
              Suppression list
            </span>
          }
          description="Numbers this client's messages skip: blocked by hand, opted out, or reported as Do Not Disturb (promotional messages only)."
        />
        <CardBody className="space-y-4">
          <Switch
            label={enabled ? 'On for this client' : 'Off for this client'}
            checked={enabled} disabled={toggle.isPending}
            hint={
              enabled
                ? `${clientName} manages its own list in the portal, and every send, test and scheduled run skips the numbers on it.`
                : `${clientName} sees nothing about it, and sends are not checked against it. Numbers already on the list are kept for when it is turned back on.`
            }
            onChange={(event) => toggle.mutate(event.target.checked)}
          />
  
          {!enabled && (settings.data?.count ?? 0) > 0 ? (
            <Alert tone="info">
              {formatNumber(settings.data?.count ?? 0)} numbers are on the list and are not being applied while it is off.
            </Alert>
          ) : null}
  
          <Can permission="messaging.suppression.create">
            <div className="grid gap-3 md:grid-cols-[1fr_12rem_auto] md:items-end">
              <Textarea
                label="Add numbers"
                rows={2}
                placeholder="98480 22338, 97014 50362"
                hint="Separated by commas, spaces or new lines. Up to 5,000."
                value={numbers}
                onChange={(event) => setNumbers(event.target.value)}
              />
              <Select
                label="Why"
                value={reason}
                options={[
                  { value: 'manual', label: 'Blocked' },
                  { value: 'stop', label: 'Opted out' },
                ]}
                onChange={(event) => setReason(event.target.value as 'manual' | 'stop')}
              />
              <Button
                className="md:mb-5"
                loading={add.isPending}
                disabled={!numbers.trim()}
                onClick={() => add.mutate()}
              >
                Add
              </Button>
            </div>
            {add.error instanceof ApiError ? <p className="text-xs text-danger">{add.error.message}</p> : null}
          </Can>
  
          <ServerTable<SuppressionRow>
            columns={columns}
            fetchRows={fetchRows}
            rowKey={(row) => row.public_id}
            queryKey={listKey}
            stateKey="messaging.suppressions"
            searchPlaceholder="Search by mobile"
            fillHeight={false}
            emptyTitle="Nobody is suppressed"
            emptyDescription="Numbers added here, by the client, or learned from Do Not Disturb reports appear here."
          />
        </CardBody>
  
        <Confirm
          open={removing !== null}
          onClose={() => setRemoving(null)}
          title={`Remove ${removing?.mobile ?? ''}?`}
          confirmLabel="Remove"
          message="This client's messages go to that number again."
          onConfirm={async () => {
            if (removing) await remove.mutateAsync(removing);
          }}
        />
      </Card>
    </ReadOnly>
  );
}
